import {Component, OnInit, Renderer2} from "@angular/core";
import {RouterLink} from "@angular/router";
import {IntraDoOperationApi, IntraDoSinistre, IntraDoSinistreApi} from "../../../../../shared/sdk";
import {NgxSpinnerService} from "ngx-spinner";
import {AppToasterService} from '../../../../../core/services/app.toaster.service';
import {AppGlobalEventManagerService} from '../../../../../core/services/app.globalEventManager.service';
import {AppSessionService} from '../../../../../core/services/app.session.service';
import {ModalCompatibilityService} from '../../../../../core/services/modal-compatibility.service';
import {NgClass} from '@angular/common';
import {Tooltip} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {Drawer} from 'primeng/drawer';
import {JplusBlocked, JplusWarning} from '../../../models/JPlus';
import {PeopleWithrightsInterface} from '../../../../../core/interfaces/peopleWithrights.interface';
import {HasRightPipe} from '../../../../../core/pipes/hasRight.pipe';
import {TimesheetSliderPartial} from '../../../../../core/directives/partials/timesheetSlider/timesheetSlider.partial';
import moment from 'moment';
import { environment } from "../../../../../../environments/environment";
import { DestroyableComponent } from "../../../../../core/classes/destroyable.component";
import {ChronoRefPipe} from '../../../../../core/pipes/chronoRef.pipe';

/**
 * Code de cause renvoyé par le back (Api/common/sinistreAnomaliesRules.js).
 * Aligné sur le type front `anomalieCauseCode` de la page DOstat/anomalies.
 */
export type AnomalieCauseCode =
  | 'INDEMNITES_DIFF_ENJEU'
  | 'POSITION_NON_DEFINIE'
  | 'POSITION_NEGATIVE'
  | 'ETAT_NON_FINAL'
  | 'DOSSIER_OUVERT_60J_SANS_POSITION'
  | 'BILAN_OBSOLETE'
  | 'DEVIS_MANQUANT'
  | 'DOCUMENTATIONS_NON_CATEGORISEES';

export type AnomalieSeverity = 'danger' | 'warning' | 'info' | 'success';

export interface AnomalieCause {
  code: AnomalieCauseCode | string;
  label: string;
  severity: AnomalieSeverity;
  category?: string;
  priority?: number;
  /** Si true, bloque la sortie de la fiche sinistre via le CanDeactivateGuard. */
  blockLeave?: boolean;
  detail?: string | null;
}

@Component({
  selector: 'sinistreSlider',
  standalone: true,
  imports: [
    NgClass,
    Tooltip,
    TranslocoPipe,
    RouterLink,
    Drawer,
    HasRightPipe,
    ChronoRefPipe,
],
  templateUrl: './sinistreSlider.partial.html'
})
export class SinistreSliderPartial extends DestroyableComponent implements OnInit{

  public currentSinistre: IntraDoSinistre;

  colRight2IsActive = false;

  // Liste des anomalies détectées sur le sinistre courant (forme unifiée backend).
  public causes: AnomalieCause[] = [];
  // Compteurs par sévérité — pilote la pastille du handle + l'en-tête du drawer.
  public nbAnomalies = 0;
  public nbDanger = 0;
  public nbWarning = 0;
  public nbInfo = 0;

  public user: PeopleWithrightsInterface;

  public start!: Date;
  public end!: Date;

  /**
   * Mapping code de cause → classes Tailwind du badge (texte + fond + bordure).
   * Cohérent avec la page DOstat/anomalies pour garder une identité visuelle commune.
   */
  public causeBadgeMap: Record<string, {icon: string, classes: string}> = {
    INDEMNITES_DIFF_ENJEU:           {icon: 'fa-light fa-scale-unbalanced',  classes: 'text-red-700 bg-red-50 border-red-200'},
    POSITION_NON_DEFINIE:            {icon: 'fa-light fa-circle-question',   classes: 'text-amber-700 bg-amber-50 border-amber-200'},
    POSITION_NEGATIVE:               {icon: 'fa-light fa-thumbs-down',       classes: 'text-rose-700 bg-rose-50 border-rose-200'},
    ETAT_NON_FINAL:                  {icon: 'fa-light fa-flag',              classes: 'text-indigo-700 bg-indigo-50 border-indigo-200'},
    DOSSIER_OUVERT_60J_SANS_POSITION:{icon: 'fa-light fa-hourglass-half',    classes: 'text-sky-700 bg-sky-50 border-sky-200'},
    BILAN_OBSOLETE:                  {icon: 'fa-light fa-file-chart-pie',    classes: 'text-orange-700 bg-orange-50 border-orange-200'},
    DEVIS_MANQUANT:                  {icon: 'fa-light fa-file-invoice',      classes: 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200'},
    DOCUMENTATIONS_NON_CATEGORISEES: {icon: 'fa-light fa-folder-tree',       classes: 'text-slate-700 bg-slate-50 border-slate-200'},
  };

  /** Petit chip de sévérité affiché à droite de chaque carte. */
  public severityChip: Record<AnomalieSeverity, {label: string, classes: string}> = {
    danger:  {label: 'Critique',    classes: 'bg-red-600 text-white'},
    warning: {label: 'Attention',   classes: 'bg-amber-500 text-white'},
    info:    {label: 'Info',        classes: 'bg-sky-500 text-white'},
    success: {label: 'OK',          classes: 'bg-emerald-500 text-white'},
  };

  constructor(
    public _toastr: AppToasterService,
    public renderer2: Renderer2,
    public spinner: NgxSpinnerService,
    public appGlobalEventManagerService: AppGlobalEventManagerService,
    private _ebOperationApiService: IntraDoOperationApi,
    public intraSinistreService: IntraDoSinistreApi,
    public _sessionService: AppSessionService,
    public modalService: ModalCompatibilityService,
  ) {
    super();

    this.subscribe(this._sessionService.logguedAccountEmitter, (people) => {
      this.user = people;
    });

    this.subscribe(this._sessionService._currentSinistre, (sinistre: IntraDoSinistre) => {
      this.currentSinistre = sinistre;
      this.refreshCauses(sinistre);
    });
  }

  /**
   * Met à jour la liste des causes + compteurs par sévérité à partir du sinistre.
   * `sinistre.causes` est posé par helper.getAlertesSinistres côté API (cf. catalogue
   * sinistreAnomaliesRules.js, partagé avec la page DOstat/anomalies).
   */
  private refreshCauses(sinistre: IntraDoSinistre | null) {
    const raw = (sinistre as any)?.causes as AnomalieCause[] | undefined;
    this.causes = Array.isArray(raw) ? raw.slice().sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99)) : [];
    this.nbAnomalies = this.causes.length;
    this.nbDanger  = this.causes.filter(c => c.severity === 'danger').length;
    this.nbWarning = this.causes.filter(c => c.severity === 'warning').length;
    this.nbInfo    = this.causes.filter(c => c.severity === 'info').length;
  }

  toggleSidebar2(){
    this.colRight2IsActive = !this.colRight2IsActive;
  }

  /** Classes du badge compteur dans la poignée (latérale). */
  public handleBadgeClasses(): string {
    if (this.nbDanger > 0)  return 'bg-red-600 animate-pulse';
    if (this.nbWarning > 0) return 'bg-amber-500';
    if (this.nbInfo > 0)    return 'bg-sky-500';
    return 'bg-emerald-500';
  }

  /** Tooltip de la poignée : récap rapide des anomalies par sévérité. */
  public handleTooltip(): string {
    if (this.nbAnomalies === 0) return 'Aucune anomalie détectée';
    const parts: string[] = [];
    if (this.nbDanger)  parts.push(this.nbDanger  + ' critique(s)');
    if (this.nbWarning) parts.push(this.nbWarning + ' attention');
    if (this.nbInfo)    parts.push(this.nbInfo    + ' info');
    return parts.join(' · ');
  }

  ngOnInit() {
    this.start = moment().utc().startOf('month').toDate();
    this.end = moment().utc().endOf('month').toDate();
  }

  protected readonly JplusBlocked = JplusBlocked;
  protected readonly JplusWarning = JplusWarning;
  protected readonly environment = environment;
}
